import * as React from 'react';

import type { HostedFieldsFieldDataFields } from 'braintree-web/hosted-fields';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import user from 'js/lib/user';

import { storeCreditCardToWallet } from 'bundles/account-settings/actions/EnterCardFormActions';
import SaveButton from 'bundles/account-settings/components/common/SaveButton';
import StatusMessage from 'bundles/account-settings/components/common/StatusMessage';
import EnterCardForm from 'bundles/account-settings/components/sections/payment-method/EnterCardForm';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { RecaptchaClient } from 'bundles/common/components/RecaptchaEnterprise';
import RecaptchaEnterprise from 'bundles/common/components/RecaptchaEnterprise';
import paymentsExperiments from 'bundles/epic/clients/payments';
import { PAYMENTS_ENTERPRISE_RECAPTCHA_CHECKBOX_SITE_KEY } from 'bundles/payments-common/common/constants';
import { errorMapping } from 'bundles/payments/common/constants';
import type { BillingInfo } from 'bundles/payments/common/types';
import PaymentErrors from 'bundles/payments/components/PaymentErrors';
import { getFormValidationErrors, isFormValid } from 'bundles/payments/utils/formValidation';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/EnterCard';

type Props = {
  storeToWalletSuccess?: boolean;
  storeToWalletErrorMessage?: string;
  braintreeHostedFieldsData?: HostedFieldsFieldDataFields;
  billingInfo: BillingInfo;
};

type State = {
  updateStatus: string | null;
  statusUpdateErrorMessage: string | null;
  isSubmitted: boolean;
  formValidationErrorCode: string | null;
  disableSaveButtonForUnverifiedUser: boolean;

  recaptchaClient?: RecaptchaClient;
  didRecaptchaSucceed: boolean;
};

class EnterCard extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    _eventData: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof EnterCard.contextTypes>;

  state: State = {
    updateStatus: null,
    statusUpdateErrorMessage: null,
    isSubmitted: false,
    didRecaptchaSucceed: false,
    formValidationErrorCode: null,
    disableSaveButtonForUnverifiedUser: false,
  };

  componentDidMount(): void {
    const isRequireEmailVerification = paymentsExperiments.get('requireEmailVerification');
    const disableSaveButtonForUnverifiedUser = isRequireEmailVerification && !user.get().email_verified;
    this.setState({ disableSaveButtonForUnverifiedUser });

    if (disableSaveButtonForUnverifiedUser) {
      this.setState({ formValidationErrorCode: errorMapping.unverifiedEmail });
    }
  }

  componentDidUpdate(prevProps: $TSFixMe, prevState: $TSFixMe) {
    const { storeToWalletSuccess, storeToWalletErrorMessage } = this.props;

    if (!prevProps.storeToWalletSuccess && storeToWalletSuccess) {
      this.setState({ updateStatus: UpdateStates.SUCCEEDED });
    } else if (!prevProps.storeToWalletErrorMessage && storeToWalletErrorMessage) {
      this.state.recaptchaClient?.reset();

      this.setState({
        updateStatus: UpdateStates.FAILED,
        statusUpdateErrorMessage: storeToWalletErrorMessage,
      });
    }
  }

  onClickSaveButton = (ev: React.SyntheticEvent<any>): void => {
    ev.preventDefault();
    this.saveCard();
  };

  saveCard = () => {
    const { executeAction } = this.context;
    const { billingInfo, braintreeHostedFieldsData } = this.props;

    const recaptchaToken = this.state.recaptchaClient?.getToken();

    // Recaptcha will retry if it fails
    if (!recaptchaToken) {
      return;
    }

    this.setState(() => ({ isSubmitted: true }));
    if (!isFormValid('braintree', billingInfo, braintreeHostedFieldsData)) {
      this.setState({
        formValidationErrorCode: errorMapping.invalidFields,
      });
      return;
    }

    this.setState({ updateStatus: UpdateStates.UPDATING });
    executeAction(storeCreditCardToWallet, { recaptchaToken });
  };

  recaptchaOnSuccessHandler = (token: string) => {
    this.setState(() => ({ didRecaptchaSucceed: true }));
  };

  recaptchaOnReadyHandler = (recaptchaClient: RecaptchaClient) => {
    this.setState(() => ({ recaptchaClient }));
  };

  recaptchaOnErrorHandler = () => {
    this.setState(() => ({ didRecaptchaSucceed: false }));
  };

  render() {
    const {
      updateStatus,
      statusUpdateErrorMessage,
      isSubmitted,
      didRecaptchaSucceed,
      formValidationErrorCode,
      disableSaveButtonForUnverifiedUser,
    } = this.state;
    const { braintreeHostedFieldsData, billingInfo } = this.props;

    return (
      <div className="rc-EnterCard">
        {formValidationErrorCode && (
          <PaymentErrors
            submissionErrorCode={formValidationErrorCode}
            submissionErrorCodeMessageProps={{
              validationErrors: getFormValidationErrors('braintree', billingInfo, braintreeHostedFieldsData),
            }}
          />
        )}
        <EnterCardForm isSubmitted={isSubmitted} />
        <div className="recaptcha-container">
          <RecaptchaEnterprise
            siteKey={PAYMENTS_ENTERPRISE_RECAPTCHA_CHECKBOX_SITE_KEY}
            onSuccess={this.recaptchaOnSuccessHandler}
            onReady={this.recaptchaOnReadyHandler}
            onError={this.recaptchaOnErrorHandler}
          />
        </div>

        <div className="save-button-wrapper horizontal-box align-items-vertical-center">
          {updateStatus !== UpdateStates.SUCCEEDED && (
            <SaveButton
              onClick={this.onClickSaveButton}
              text={_t('Save')}
              updating={updateStatus === UpdateStates.UPDATING}
              disabled={!didRecaptchaSucceed || disableSaveButtonForUnverifiedUser}
            />
          )}
          {updateStatus && (
            <StatusMessage
              status={updateStatus}
              // @ts-expect-error TSMIGRATION
              customMessage={updateStatus === UpdateStates.FAILED ? statusUpdateErrorMessage : ''}
            />
          )}
        </div>
      </div>
    );
  }
}

// TODO: connectToStores<Props, InputProps, Stores>
export default connectToStores<any, any>(['EnterCardFormStore'], ({ EnterCardFormStore }, props) => {
  return {
    ...props,
    storeToWalletSuccess: EnterCardFormStore.didSuccessfullyStoreToWallet(),
    storeToWalletErrorMessage: EnterCardFormStore.getStoreToWalletErrorMessage(),
    billingInfo: EnterCardFormStore.getFormattedBillingInfo(),
    braintreeHostedFieldsData: EnterCardFormStore.getBraintreeHostedFields()?.getState().fields,
  };
})(EnterCard);
